(() => {
  if (customElements.get('tl-cart-drawer')) return;
  class TeslongCartDrawer extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.dialog = this.querySelector('dialog');
      this.root = this.dataset.root || '/';
      this.messages = this.querySelector('[data-tlc-messages]').dataset;
      this.state = JSON.parse(this.querySelector('[data-tlc-state]').textContent);
      this.note = this.querySelector('#tlc-note');
      this.noteDialog = this.querySelector('#tlc-note-dialog');
      this.optionsDialog = this.querySelector('#tlc-options-dialog');
      this.savedNote = this.note.value;
      this.queue = Promise.resolve();
      this.pending = 0;
      this.codes = [];
      this.recommendationVersion = 0;
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.carousel = this.querySelector('[data-tlc-recommendation-list]');
      this.carousel.addEventListener('scroll', () => this.updateCarousel(), { passive: true, ...options });
      this.carousel.addEventListener('keydown', event => {
        if (event.target !== this.carousel || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        this.slide(event.key === 'ArrowRight' ? 1 : -1);
      }, options);
      this.carouselResize = new ResizeObserver(() => this.updateCarousel());
      this.carouselResize.observe(this.carousel);
      this.noteDialog.addEventListener('close', () => this.querySelector('[data-tlc-note-open]').focus(), options);
      this.optionsDialog.addEventListener('close', () => {
        (this.optionsTrigger?.isConnected ? this.optionsTrigger : this.querySelector('[data-tlc-close]')).focus();
      }, options);
      this.addEventListener('click', event => this.onClick(event), options);
      this.addEventListener('change', event => this.onChange(event), options);
      this.addEventListener('submit', event => this.onSubmit(event), options);
      this.note.addEventListener('input', () => {
        this.lock();
        this.querySelector('[data-tlc-note-status]').textContent = this.messages.saving;
        clearTimeout(this.noteTimer);
        this.noteTimer = setTimeout(() => this.run(() => this.saveNote()), 450);
      }, options);
      this.dialog.addEventListener('close', () => {
        document.documentElement.classList.remove('tlc-open');
        this.trigger?.focus();
      }, options);
      this.dialog.addEventListener('click', event => {
        if (event.target !== this.dialog) return;
        const rect = this.dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right) this.dialog.close();
      }, options);
      document.addEventListener('click', event => {
        const trigger = event.target.closest('.site-header-cart--button, [data-tlc-open]');
        if (!trigger || event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        this.open(trigger);
      }, { capture: true, ...options });
      window.addEventListener('cartcount:update', () => {
        document.dispatchEvent(new Event('closeFlyouts'));
        this.open();
      }, options);
      document.addEventListener('tl:cart:open', () => this.open(), options);
      document.addEventListener('submit', event => {
        const form = event.target;
        if (!(form instanceof HTMLFormElement) || this.contains(form)) return;
        const action = new URL(form.action, location.href);
        if (action.origin !== location.origin || !/\/cart\/add(?:\.js)?\/?$/.test(action.pathname)) return;
        if (event.submitter?.name === 'checkout' || event.submitter?.closest('.shopify-payment-button')) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        if (form.dataset.tlcAdding) return;
        const body = new FormData(form);
        body.delete('return_to');
        const button = event.submitter;
        form.dataset.tlcAdding = 'true';
        if (button) button.disabled = true;
        this.run(async () => {
          try {
            await this.saveNote();
            await this.request(`${this.root}cart/add.js`, body);
            this.open(button);
          } catch (error) {
            this.open(button);
            // Keep the server error visible after opening/refreshing the drawer.
            this.run(() => { throw error; });
          } finally {
            delete form.dataset.tlcAdding;
            if (button) button.disabled = false;
          }
        });
      }, { capture: true, ...options });
      this.observer = new MutationObserver(() => {
        const banner = document.querySelector('[data-atc-banner][data-animation-state="open"]');
        if (banner && this.banner !== banner) {
          this.banner = banner;
          banner.querySelector('[data-atc-banner-close]')?.click();
          if (this.dialog.open) this.querySelector('[data-tlc-close]').focus();
        }
        const legacy = document.querySelector('.boost-sd__modal .boost-sd__cart--side');
        if (!legacy || this.legacy === legacy) return;
        this.legacy = legacy;
        const close = legacy.closest('.boost-sd__modal').querySelector('[aria-label="Close"]');
        if (close) { close.click(); this.open(); }
      });
      this.observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-animation-state'] });
      document.documentElement.classList.add('tlc-enabled');
    }
    disconnectedCallback() {
      this.events?.abort();
      this.observer?.disconnect();
      this.carouselResize?.disconnect();
      clearTimeout(this.noteTimer);
      document.documentElement.classList.remove('tlc-enabled', 'tlc-open');
      this.ready = false;
    }
    async request(path, body) {
      const response = await fetch(path, {
        credentials: 'same-origin', cache: 'no-store',
        ...(body ? { method: 'POST', headers: body instanceof FormData ? { Accept: 'application/json' } : { 'Content-Type': 'application/json', Accept: 'application/json' }, body: body instanceof FormData ? body : JSON.stringify(body) } : {}),
        signal: AbortSignal.timeout(15000)
      });
      if (!response.ok) {
        console.warn('[Teslong cart] Request failed', response.status, new URL(path, location.origin).pathname);
        let message = this.messages.error;
        try { const error = await response.json(); message = typeof error.description === 'string' ? error.description : message; } catch {}
        throw new Error(message);
      }
      return response;
    }
    status(message, error = false) {
      const element = this.querySelector('[data-tlc-status]');
      element.textContent = message;
      element.toggleAttribute('data-error', error);
      if (error && this.noteDialog.open) this.querySelector('[data-tlc-note-status]').textContent = message;
      if (error && this.optionsDialog.open) this.querySelector('[data-tlc-options-status]').textContent = message;
    }
    lock() {
      const busy = this.pending > 0;
      this.dialog.setAttribute('aria-busy', String(busy));
      this.querySelectorAll('[data-tlc-items] button, [data-tlc-items] input, [data-tlc-discount] button, [data-tlc-discount] input, [data-tlc-discount-jump], [data-tlc-add], [data-tlc-options-open], [data-tlc-variant], [data-tlc-checkout] > button').forEach(el => { el.disabled = busy; });
      const selected = this.querySelector('[data-tlc-variant]')?.selectedOptions[0];
      this.querySelector('[data-tlc-options-add]').disabled = busy || !selected || selected.disabled;
      const wallets = this.querySelector('[data-tlc-wallets]');
      if (wallets) wallets.inert = busy || this.note.value !== this.savedNote;
    }
    run(action) {
      this.pending++;
      this.lock();
      const next = this.queue.then(action);
      this.queue = next.catch(error => this.status(error.message || this.messages.error, true)).finally(() => {
        this.pending--;
        this.lock();
      });
      return this.queue;
    }
    open(trigger) {
      if (!this.dialog.open) {
        this.trigger = trigger || document.activeElement;
        this.dialog.showModal();
        document.documentElement.classList.add('tlc-open');
        this.querySelector('[data-tlc-close]').focus();
      }
      if (this.opening) return;
      this.opening = true;
      this.run(async () => {
        this.status(this.messages.loading);
        await this.saveNote();
        await this.refresh();
        this.status('');
      }).finally(() => { this.opening = false; });
    }
    async saveNote() {
      clearTimeout(this.noteTimer);
      while (this.note.value !== this.savedNote) {
        const value = this.note.value;
        this.querySelector('[data-tlc-note-status]').textContent = this.messages.saving;
        const cart = await (await this.request(`${this.root}cart/update.js`, { note: value })).json();
        if ((cart.note || '') !== value) throw new Error(this.messages.error);
        this.savedNote = value;
      }
      this.querySelector('[data-tlc-note-status]').textContent = this.messages.saved;
      this.updateNoteButton();
    }
    updateNoteButton() {
      this.querySelector('[data-tlc-note-label]').textContent = this.savedNote ? this.messages.noteEdit : this.messages.noteAdd;
    }
    async refresh() {
      const url = new URL(this.root, location.origin);
      url.searchParams.set('sections', this.dataset.section);
      if (this.dataset.previewTheme) url.searchParams.set('preview_theme_id', this.dataset.previewTheme);
      const sections = await (await this.request(url)).json();
      const html = sections[this.dataset.section];
      if (!html) {
        console.warn('[Teslong cart] Section unavailable', this.dataset.section);
        throw new Error(this.messages.error);
      }
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector('tl-cart-drawer');
      if (!fresh) throw new Error(this.messages.error);
      const state = JSON.parse(fresh.querySelector('[data-tlc-state]').textContent);
      const focus = document.activeElement;
      const focusKey = focus?.closest('[data-key]')?.dataset.key;
      const focusAttribute = ['data-tlc-step', 'data-tlc-remove', 'data-tlc-quantity'].find(name => focus?.hasAttribute(name));
      const focusValue = focusAttribute && focus.getAttribute(focusAttribute);
      for (const selector of ['[data-tlc-items]', '[data-tlc-footer]', '[data-tlc-shipping]']) {
        this.querySelector(selector).replaceChildren(...fresh.querySelector(selector).childNodes);
      }
      if (this.note.value === this.savedNote) {
        this.note.value = state.note || '';
        this.savedNote = this.note.value;
      }
      this.updateNoteButton();
      this.state = state;
      this.querySelector('[data-tlc-count]').textContent = state.count;
      this.querySelector('[data-tlc-extras]').hidden = !state.count;
      this.querySelector('[data-tlc-footer]').hidden = !state.count;
      document.querySelectorAll('[data-header-cart-count]').forEach(el => {
        el.dataset.headerCartCount = state.count || '';
        el.classList.toggle('visible', state.count > 0);
      });
      const cart = await (await this.request(`${this.root}cart.js`)).json();
      this.codes = (cart.discount_codes || []).filter(code => code.applicable).map(code => code.code);
      this.renderCodes();
      this.lock();
      if (focusKey && focusAttribute && this.dialog.open) {
        const item = [...this.querySelectorAll('[data-key]')].find(el => el.dataset.key === focusKey);
        const target = item && [...item.querySelectorAll(`[${focusAttribute}]`)].find(el => el.getAttribute(focusAttribute) === focusValue);
        (target || this.querySelector('[data-tlc-close]')).focus();
      }
      this.loadRecommendations();
      document.dispatchEvent(new CustomEvent('tl:cart:updated', { detail: { item_count: state.count } }));
    }
    renderCodes() {
      const list = this.querySelector('[data-tlc-codes]');
      list.replaceChildren();
      for (const code of this.codes) {
        const chip = document.createElement('span');
        chip.className = 'tlc__code';
        chip.append(document.createTextNode(code));
        const remove = document.createElement('button');
        remove.type = 'button';
        remove.textContent = '×';
        remove.dataset.tlcRemoveCode = code;
        remove.setAttribute('aria-label', `${this.messages.remove} ${code}`);
        chip.append(remove);
        list.append(chip);
      }
    }
    async discount(code, remove = false) {
      const existing = await (await this.request(`${this.root}cart.js`)).json();
      const current = (existing.discount_codes || []).filter(entry => entry.applicable).map(entry => entry.code);
      const codes = current.filter(value => value.toLowerCase() !== code.toLowerCase());
      if (!remove) codes.push(code);
      const cart = await (await this.request(`${this.root}cart/update.js`, { discount: codes.join(',') })).json();
      const accepted = (cart.discount_codes || []).some(entry => entry.code.toLowerCase() === code.toLowerCase() && entry.applicable);
      this.querySelector('[data-tlc-discount-status]').textContent = remove ? this.messages.removed : accepted ? this.messages.applied : this.messages.invalid;
      if (!accepted && !remove) {
        await this.request(`${this.root}cart/update.js`, { discount: current.join(',') });
      }
      if (accepted || remove) this.querySelector('#tlc-discount').value = '';
      await this.refresh();
    }
    async change(key, quantity) {
      if (!Number.isInteger(quantity) || quantity < 0) { await this.refresh(); return; }
      await this.saveNote();
      try {
        await this.request(`${this.root}cart/change.js`, { id: key, quantity });
      } catch (error) {
        await this.refresh();
        throw error;
      }
      await this.refresh();
      this.status(this.messages.updated);
    }
    onClick(event) {
      const button = event.target.closest('button, a');
      if (!button || button.disabled) return;
      if (button.hasAttribute('data-tlc-options-open')) { this.openOptions(button); return; }
      if (button.hasAttribute('data-tlc-options-close')) { this.optionsDialog.close(); return; }
      if (button.hasAttribute('data-tlc-options-add')) {
        const selected = this.querySelector('[data-tlc-variant]')?.selectedOptions[0];
        if (!selected || selected.disabled) return;
        const id = Number(selected.value);
        this.run(async () => {
          this.querySelector('[data-tlc-options-status]').textContent = '';
          await this.saveNote();
          await this.request(`${this.root}cart/add.js`, { items: [{ id, quantity: 1 }] });
          this.optionsTrigger = null;
          this.optionsDialog.close();
          await this.refresh();
          this.status(this.messages.updated);
        });
        return;
      }
      if (button.hasAttribute('data-tlc-slide')) { this.slide(Number(button.dataset.tlcSlide)); return; }
      if (button.hasAttribute('data-tlc-discount-jump')) {
        const scroller = this.querySelector('.tlc__scroll');
        const form = this.querySelector('[data-tlc-discount]');
        this.querySelector('#tlc-discount').focus({ preventScroll: true });
        scroller.scrollTo({ top: scroller.scrollTop + form.getBoundingClientRect().top - scroller.getBoundingClientRect().top - 12, behavior: this.scrollBehavior() });
        return;
      }
      if (button.hasAttribute('data-tlc-note-open')) { this.noteDialog.showModal(); this.note.focus(); return; }
      if (button.hasAttribute('data-tlc-note-close')) { this.noteDialog.close(); return; }
      if (button.hasAttribute('data-tlc-note-save')) {
        this.run(async () => { await this.saveNote(); this.noteDialog.close(); });
        return;
      }
      if (button.hasAttribute('data-tlc-close')) { this.dialog.close(); return; }
      if (button.hasAttribute('data-tlc-full-cart')) {
        event.preventDefault();
        this.run(async () => { await this.saveNote(); location.assign(button.href); });
        return;
      }
      const item = button.closest('[data-key]');
      if (item && (button.hasAttribute('data-tlc-step') || button.hasAttribute('data-tlc-remove'))) {
        const quantity = button.hasAttribute('data-tlc-remove') ? 0 : Math.max(0, Number(item.querySelector('[data-tlc-quantity]').value) + Number(button.dataset.tlcStep));
        this.run(() => this.change(item.dataset.key, quantity));
      }
      if (button.hasAttribute('data-tlc-remove-code')) this.run(() => this.discount(button.dataset.tlcRemoveCode, true));
      if (button.hasAttribute('data-tlc-add')) this.run(async () => {
        await this.saveNote();
        await this.request(`${this.root}cart/add.js`, { items: [{ id: Number(button.dataset.tlcAdd), quantity: 1 }] });
        await this.refresh();
        this.status(this.messages.updated);
      });
    }
    onChange(event) {
      if (event.target.hasAttribute('data-tlc-variant')) this.updateVariant();
      if (event.target.hasAttribute('data-tlc-quantity')) {
        const input = event.target;
        this.run(() => this.change(input.closest('[data-key]').dataset.key, Number(input.value)));
      }
      if (event.target === this.note) this.run(() => this.saveNote());
    }
    onSubmit(event) {
      if (event.target.hasAttribute('data-tlc-discount')) {
        event.preventDefault();
        const code = this.querySelector('#tlc-discount').value.trim();
        if (code) this.run(() => this.discount(code));
      }
      if (event.target.hasAttribute('data-tlc-checkout')) {
        event.preventDefault();
        this.run(async () => {
          await this.saveNote();
          const form = this.querySelector('[data-tlc-checkout]');
          HTMLFormElement.prototype.submit.call(form);
        });
      }
    }
    openOptions(button) {
      const template = button.closest('[data-product-id]')?.querySelector('template[data-tlc-options]');
      if (!template) return;
      this.optionsTrigger = button;
      this.querySelector('[data-tlc-options-body]').replaceChildren(template.content.cloneNode(true));
      this.querySelector('[data-tlc-options-status]').textContent = '';
      this.updateVariant();
      this.optionsDialog.showModal();
      this.querySelector('[data-tlc-variant]').focus();
    }
    updateVariant() {
      const selected = this.querySelector('[data-tlc-variant]')?.selectedOptions[0];
      if (!selected) return;
      this.querySelector('[data-tlc-variant-price]').textContent = selected.dataset.price;
      const image = this.optionsDialog.querySelector('img');
      if (image && selected.dataset.image) { image.removeAttribute('srcset'); image.src = selected.dataset.image; }
      this.lock();
    }
    scrollBehavior() { return matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'; }
    slide(direction) {
      const card = this.carousel.firstElementChild;
      if (!card) return;
      const distance = card.getBoundingClientRect().width + 10;
      this.carousel.scrollBy({ left: direction * distance, behavior: this.scrollBehavior() });
    }
    updateCarousel() {
      const { scrollLeft, scrollWidth, clientWidth } = this.carousel;
      this.querySelector('[data-tlc-slide="-1"]').disabled = scrollLeft <= 1;
      this.querySelector('[data-tlc-slide="1"]').disabled = scrollLeft + clientWidth >= scrollWidth - 1;
    }
    async loadRecommendations() {
      const version = ++this.recommendationVersion;
      const list = this.querySelector('[data-tlc-recommendation-list]');
      const region = this.querySelector('[data-tlc-recommendations]');
      const products = this.state.products;
      if (!products.length) { list.replaceChildren(); region.hidden = true; return; }
      try {
        const cards = [];
        let heading = '';
        const seen = new Set(products.map(String));
        for (const product of products.slice(0, 3)) {
          const url = new URL(this.dataset.recommendations, location.origin);
          url.search = new URLSearchParams({ product_id: product, section_id: 'tl-cart-recommendations', intent: 'complementary', limit: '3' });
          if (this.dataset.previewTheme) url.searchParams.set('preview_theme_id', this.dataset.previewTheme);
          const html = await (await this.request(url)).text();
          if (version !== this.recommendationVersion) return;
          const doc = new DOMParser().parseFromString(html, 'text/html');
          heading = doc.querySelector('[data-tlc-recommendation-results]')?.dataset.heading || heading;
          for (const card of doc.querySelectorAll('[data-tlc-recommendation-results] [data-product-id]')) {
            if (seen.has(card.dataset.productId)) continue;
            seen.add(card.dataset.productId);
            cards.push(card);
          }
          if (cards.length >= 3) break;
        }
        if (!cards.length && this.dataset.related === 'true') {
          const url = new URL(this.dataset.recommendations, location.origin);
          url.search = new URLSearchParams({ product_id: products[0], section_id: 'tl-cart-recommendations', intent: 'related', limit: '3' });
          if (this.dataset.previewTheme) url.searchParams.set('preview_theme_id', this.dataset.previewTheme);
          const html = await (await this.request(url)).text();
          if (version !== this.recommendationVersion) return;
          const doc = new DOMParser().parseFromString(html, 'text/html');
          heading = doc.querySelector('[data-tlc-recommendation-results]')?.dataset.heading || heading;
          for (const card of doc.querySelectorAll('[data-tlc-recommendation-results] [data-product-id]')) {
            if (seen.has(card.dataset.productId)) continue;
            seen.add(card.dataset.productId);
            cards.push(card);
          }
        }
        if (heading) this.querySelector('#tlc-recommendations-title').textContent = heading;
        list.replaceChildren(...cards.slice(0, 3));
        region.hidden = cards.length === 0;
        this.carousel.scrollLeft = 0;
        this.updateCarousel();
        this.lock();
      } catch {
        if (version === this.recommendationVersion) { list.replaceChildren(); region.hidden = true; }
      }
    }
  }
  customElements.define('tl-cart-drawer', TeslongCartDrawer);
})();
